import { IAppReducer, IDetailStep } from 'models';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getUserSubscriptions,
  IPayment,
  Subscription,
} from 'shared/services/user-subscription';

interface UseSubscriptionsReturn {
  steps: IDetailStep[];
  navigate: ReturnType<typeof useNavigate>;
  user: IAppReducer['authReducer'];
  subscriptions: Subscription[];
  loading: boolean;
  error: boolean;
  showAlertSuccess: boolean;
  setShowAlertSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  refreshSubscriptions: () => Promise<void>;
  payments: IPayment[];
}

const STEPS: IDetailStep[] = [
  {
    description: 'ajustes',
    state: 'active',
    link: '/settings',
    permissionToNavigate: true,
  },
  {
    description: 'suscripciones',
    state: 'active',
    link: '/settings/subscriptions',
    permissionToNavigate: false,
  },
];

export const useSubscriptions = (): UseSubscriptionsReturn => {
  const navigate = useNavigate();
  const { authReducer: user } = useSelector((state: IAppReducer) => state);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [payments, setPayments] = useState<IPayment[]>([]);

  const location = useLocation();
  const [showAlertSuccess, setShowAlertSuccess] = useState<boolean>(
    Boolean(location.state?.showAlertSuccess)
  );
  const isMountedRef = useRef(true);

  const refreshSubscriptions = async () => {
    setLoading(true);
    setError(false);
    try {
      const { subscriptions, payments } = await getUserSubscriptions(user.userId);
      if (!isMountedRef.current) return;
      setSubscriptions(subscriptions);
      setPayments(payments);
    } catch (err) {
      if (!isMountedRef.current) return;
      setError(true);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const { subscriptions, payments } = await getUserSubscriptions(user.userId);
        if (!cancelled) {
          setSubscriptions(subscriptions);
          setPayments(payments);
        }
      } catch (err) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user.userId]);

  useEffect(() => {
    if (!showAlertSuccess) return;
    const timeoutId = setTimeout(() => {
      setShowAlertSuccess(false);
    }, 16000);
    return () => clearTimeout(timeoutId);
  }, [showAlertSuccess]);

  return {
    steps: STEPS,
    navigate,
    user,
    subscriptions,
    loading,
    error,
    showAlertSuccess,
    setShowAlertSuccess,
    refreshSubscriptions,
    payments,
  };
};
